import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useCreateMeasurementFamily, useUpdateMeasurementFamily } from '../../hooks';
import type { CatalogLanguage, MeasurementFamily, MeasurementUnitInput } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { LocalizedFieldsEditor } from './LocalizedFieldsEditor';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';
import { MeasurementUnitsEditor } from './MeasurementUnitsEditor';

interface MeasurementFamilyFormDialogProps {
  open: boolean;
  /** The family being edited (full detail — the list endpoint already returns it), or `null` to create. */
  editing: MeasurementFamily | null;
  languages: CatalogLanguage[];
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Create / edit a measurement family (unit-of-measure family), user-story-catalog-013. Standard,
 * platform-seeded families (WEIGHT/LENGTH/VOLUME/AREA/DURATION) never open this dialog for edit —
 * the page routes their row straight to a lock icon — but this component defensively renders
 * fully read-only if `editing.standard` is ever true.
 */
export function MeasurementFamilyFormDialog({
  open,
  editing,
  languages,
  canManage,
  onClose,
  onResult,
}: MeasurementFamilyFormDialogProps) {
  const { t } = useTranslation('catalog');
  const create = useCreateMeasurementFamily();
  const update = useUpdateMeasurementFamily();
  const isEdit = editing !== null;
  const isStandard = editing?.standard === true;
  const readOnly = !canManage || isStandard;
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);
  const [standardUnitCode, setStandardUnitCode] = useState('');
  const [units, setUnits] = useState<MeasurementUnitInput[]>([]);
  const [labelValues, setLabelValues] = useState<LocalizedFieldValue[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setActive(editing?.active ?? true);
    setStandardUnitCode(editing?.standardUnitCode ?? '');
    setUnits(
      (editing?.units ?? []).map((unit) => ({
        code: unit.code,
        conversionFactor: unit.conversionFactor,
        sortOrder: unit.sortOrder,
      })),
    );
    setLabelValues(
      (editing?.translations ?? []).map((translation) => ({
        locale: translation.locale,
        text: translation.label,
      })),
    );
  }, [open, editing]);

  const existingCodes = useMemo(
    () => new Set((editing?.units ?? []).map((unit) => unit.code)),
    [editing],
  );

  const defaultLanguage = useMemo(() => languages.find((l) => l.defaultLocale), [languages]);
  const defaultLabelFilled =
    !defaultLanguage ||
    labelValues.some((v) => v.locale === defaultLanguage.locale && v.text.trim() !== '');
  const unitsValid =
    units.length > 0 && units.every((u) => u.code.trim() !== '' && u.conversionFactor > 0);
  const valid =
    !isStandard &&
    code.trim() !== '' &&
    defaultLabelFilled &&
    unitsValid &&
    (isEdit || (standardUnitCode !== '' && units.some((u) => u.code === standardUnitCode)));

  const submit = () => {
    if (!valid) return;
    const translations = labelValues.map((v) => ({ locale: v.locale, label: v.text }));
    const handlers = {
      onSuccess: () => {
        onResult(
          'success',
          isEdit ? t('measurementFamilyFormDialog.saved') : t('measurementFamilyFormDialog.created'),
        );
        onClose();
      },
      onError: (error: NormalizedApiError) => {
        if (error.code === 'UNIT_LIMIT_EXCEEDED') {
          onResult('error', t('measurementFamilyFormDialog.errors.unitLimitExceeded'));
          return;
        }
        if (error.code === 'STANDARD_FAMILY_READONLY') {
          onResult('error', t('measurementFamilyFormDialog.errors.standardReadonly'));
          return;
        }
        onResult('error', error.message);
      },
    };
    if (isEdit && editing) {
      update.mutate({ code: editing.code, body: { active, units, translations } }, handlers);
      return;
    }
    create.mutate({ code: code.trim(), standardUnitCode, units, translations }, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('measurementFamilyFormDialog.editTitle') : t('measurementFamilyFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {isStandard ? (
            <Alert severity="info">{t('measurementFamilyFormDialog.standardReadonlyNotice')}</Alert>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label={t('measurementFamilyFormDialog.codeLabel')}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={code.trim() === ''}
              disabled={isEdit || readOnly}
              autoFocus={!isEdit}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            {isEdit ? (
              <FormControlLabel
                control={
                  <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={readOnly} />
                }
                label={t('measurementFamilyFormDialog.activeLabel')}
                sx={{ flexShrink: 0 }}
              />
            ) : null}
          </Stack>

          <LocalizedFieldsEditor
            languages={languages}
            values={labelValues}
            onChange={setLabelValues}
            fieldLabel={t('measurementFamilyFormDialog.labelFieldLabel')}
            disabled={readOnly}
          />

          <Divider />

          <MeasurementUnitsEditor
            units={units}
            existingCodes={existingCodes}
            standardUnitCode={standardUnitCode}
            onChange={setUnits}
            disabled={readOnly}
          />

          {!isEdit ? (
            <TextField
              select
              label={t('measurementFamilyFormDialog.standardUnitLabel')}
              value={standardUnitCode}
              onChange={(e) => setStandardUnitCode(e.target.value)}
              error={standardUnitCode === ''}
              helperText={t('measurementFamilyFormDialog.standardUnitHelper')}
              disabled={readOnly || units.length === 0}
              fullWidth
            >
              {units
                .filter((u) => u.code.trim() !== '')
                .map((unit) => (
                  <MenuItem key={unit.code} value={unit.code}>
                    {unit.code}
                  </MenuItem>
                ))}
            </TextField>
          ) : (
            <TextField
              label={t('measurementFamilyFormDialog.standardUnitLabel')}
              value={standardUnitCode}
              disabled
              fullWidth
              helperText={t('measurementFamilyFormDialog.standardUnitImmutableHelper')}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending || readOnly}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
